import {
    type GuidanceSession,
    GuidanceSessionStatus,
    GuidanceSessionType,
    type SubscriptionProduct,
} from '@fruitful/types';
import { isPast, parseISO } from 'date-fns';

/**
 * Canceled and no-show sessions are omitted from member-facing lists and do
 * not occupy a check-in slot. SMS confirmation stores CONFIRMED in place of
 * SCHEDULED, so a confirmed session stays included.
 */
export const isVisibleGuidanceSessionStatus = (status: string): boolean =>
    status !== GuidanceSessionStatus.CANCELED &&
    status !== GuidanceSessionStatus.NO_SHOWED;

/**
 * Maps guidance session types to their default user-facing display names
 * TODO: Defaults should switch from pre-Commercial-Model-Evolution to current, when safe.
 */
const GUIDANCE_SESSION_TYPE_DISPLAY_NAMES: Record<GuidanceSessionType, string> =
    {
        [GuidanceSessionType.DISCOVERY]: 'Discovery',
        [GuidanceSessionType.ROADMAP]: 'Money Map',
        [GuidanceSessionType.BUILDING_WEALTH]: 'Optimization',
        [GuidanceSessionType.QUICK_CHECK_IN]: 'Check-In',
        // --- Legacy Sessions below here (maintained for displaying member's guidance session history)
        [GuidanceSessionType.FREE_INTRO_SESSION]: 'Intro Session',
        [GuidanceSessionType.WELCOME_CALL]: 'Hello',
        [GuidanceSessionType.MEMBER_CALL]: 'Member Call',
        [GuidanceSessionType.INVESTMENT_MANAGEMENT]: 'Member Call',
        [GuidanceSessionType.REVIEW_CAPSTONE]: 'Money Milestone',
    };

const getProductGuidanceSessionDisplayName = (
    sessionType: GuidanceSessionType | `${GuidanceSessionType}`,
    product: SubscriptionProduct,
): string | undefined => {
    const onboardingName = product?.guidance.onboardingSessions.find(
        (session) => session.guidanceSessionType === sessionType,
    )?.displayName;
    if (onboardingName != null) {
        return onboardingName;
    }

    return product?.guidance.generalSessions.find(
        (session) => session.guidanceSessionType === sessionType,
    )?.displayName;
};

/**
 * Gets the user-facing display name for a guidance session type.
 * When a subscription product is provided, prefer that catalog's
 * onboarding / general session displayName (e.g. Kickoff vs Discovery).
 */
export function getGuidanceSessionTypeDisplayName(
    sessionType: `${GuidanceSessionType}`,
    product: SubscriptionProduct | null | undefined,
): string {
    const defaultDisplayName = GUIDANCE_SESSION_TYPE_DISPLAY_NAMES[sessionType];

    if (product == null) {
        return defaultDisplayName;
    }

    const productDisplayName = getProductGuidanceSessionDisplayName(
        sessionType,
        product,
    );
    return productDisplayName ?? defaultDisplayName;
}

/**
 * Used only when `product.guidance.onboardingSessions` is missing.
 * Matches the legacy catalog, not the current expected sequence
 * (Kickoff, Optimize), because that is where edge cases are more likely
 */
const FALLBACK_ONBOARDING_ORDER: GuidanceSessionType[] = [
    GuidanceSessionType.DISCOVERY,
    GuidanceSessionType.ROADMAP,
    GuidanceSessionType.BUILDING_WEALTH,
];

const getMembershipOnboardingSessionTypes = (
    product: SubscriptionProduct | null | undefined,
): GuidanceSessionType[] =>
    product?.guidance.onboardingSessions.map(
        (session) => session.guidanceSessionType,
    ) ?? [];

export const getGuidanceOnboardingOrder = (
    product?: SubscriptionProduct | null,
): GuidanceSessionType[] => {
    const fromProduct = getMembershipOnboardingSessionTypes(product);
    if (fromProduct.length > 0) {
        return fromProduct;
    }
    return FALLBACK_ONBOARDING_ORDER;
};

const includesLateStageLegacySession = (
    sessions: GuidanceSession[],
): boolean => {
    return sessions.some(
        (session) =>
            session.type === GuidanceSessionType.INVESTMENT_MANAGEMENT ||
            session.type === GuidanceSessionType.REVIEW_CAPSTONE,
    );
};

/**
 * Next type in the Guidance onboarding sequence, or null
 * if they are all done. Check-ins and other ad-hoc sessions are not
 * part of this sequence — those are booked separately after the roadmap.
 */
export const getNextGuidanceOnboardingSessionType = (
    sessions: GuidanceSession[],
    product?: SubscriptionProduct | null,
): GuidanceSessionType | null => {
    const onboardingOrder = getGuidanceOnboardingOrder(product);

    const pastSessions = sessions.filter(
        (session) =>
            isVisibleGuidanceSessionStatus(session.status) &&
            isPast(parseISO(session.startTime)),
    );

    if (pastSessions.length === 0) {
        return onboardingOrder[0] ?? null;
    }

    if (includesLateStageLegacySession(pastSessions)) {
        return null;
    }

    const furthestCompletedIndex = [...onboardingOrder]
        .map((type, index) => ({ type, index }))
        .reverse()
        .find(({ type }) =>
            pastSessions.some((session) => session.type === type),
        )?.index;

    // Unexpected case where there are past sessions, but none were found in the product onboarding list
    if (furthestCompletedIndex == null) {
        // Skip to 2nd onboarding session
        return onboardingOrder[1] ?? null;
    }

    // Returns next, or 'null' if all completed
    return onboardingOrder[furthestCompletedIndex + 1] ?? null;
};

export const hasCompletedGuidanceOnboarding = (
    sessions: GuidanceSession[],
    product?: SubscriptionProduct | null,
): boolean => {
    return getNextGuidanceOnboardingSessionType(sessions, product) == null;
};
